import { useCallback, useEffect, useState } from "react";
import "../Styles.css";
import { includes } from "lodash";
import { connect } from "react-redux";
import Sidebar from "../../components/Sidebar";
import sidebarRoutes from "../../routes";
import { Actions } from "../../internals/app/Actions";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Headerbar from "../../components/Headerbar";
import { MoonIcon, SunIcon } from "@heroicons/react/24/outline";

const applyTheme = (theme: string) => {
  document.documentElement.setAttribute("data-theme", theme);
};

// Resolve a human title for the current URL from the sidebar route table.
const getScreenTitle = (pathname: string): string => {
  for (const route of sidebarRoutes as any[]) {
    if (route.path === pathname) return route.name;
    if (route.subCategory) {
      const sub = route.subCategory.find((s: any) => s.path === pathname);
      if (sub) return sub.name;
    }
  }
  // Invoice pages carry a document id in the path; name the screen, not the id.
  if (/^\/invoices\/new$/.test(pathname)) return "New invoice";
  if (/^\/invoices\/[^/]+\/edit$/.test(pathname)) return "Edit invoice";
  if (/^\/invoices\/[^/]+$/.test(pathname)) return "Invoice";
  const seg = pathname.split("/").filter(Boolean).pop() || "Dashboard";
  return seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " ");
};

const Admin = (props: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, trialDetails, trialLoading, activeSubscriptions, selectedShop } = props;
  const [openSidebar, setOpenSidebar] = useState<boolean>(false);
  const [selectedRoute, setSelectedRoute] = useState<string>("");
  const [selectedSubRoute, setSelectedSubRoute] = useState<string>("");
  // Light mode is the default on every load; the toggle is session-only.
  const [theme, setTheme] = useState<string>("light");

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const onLocationUpdated = useCallback(
    (pathname: string) => {
      if (trialLoading) {
        return;
      }
      const isActiveSubscription = activeSubscriptions.length > 0;
      // const days = moment(trialDetails?.isExpired).diff(moment(), 'days');
      if ((!trialDetails && !isActiveSubscription) || (trialDetails?.isExpired && !isActiveSubscription)) {
        if (!includes(PathsWithoutSubscription, pathname)) {
          navigate("/signup-subscription");
        }
      }
    },
    [trialDetails, activeSubscriptions, navigate, trialLoading]
  );

  useEffect(() => {
    if (location.pathname) {
      onLocationUpdated(location.pathname);
    }
  }, [location.pathname, onLocationUpdated]);

  const toggleOpenDrawer = () => {
    setOpenSidebar(!openSidebar);
  };

  const screenTitle = getScreenTitle(location.pathname);

  return (
    <div className="w-screen h-screen flex flex-col md:flex-row items-start justify-start overflow-hidden bg-ghost-white">

      {/* Sidebar column — hidden on mobile, compact (60px) on md, full (252px) on lg+ */}
      <div className="h-full hidden md:flex flex-shrink-0 bg-surface md:w-[60px] lg:w-[252px]">
        <Sidebar
          logout={() => logout()}
          openSidebar={openSidebar}
          selectedRoute={selectedRoute}
          onSelectDashBoard={() => {}}
          selectedSubRoute={selectedSubRoute}
          toggleOpenDrawer={() => toggleOpenDrawer()}
          setSelectedRoute={(route: string) => setSelectedRoute(route)}
          setSelectedSubRoute={(route: string) => setSelectedSubRoute(route)}
        />
      </div>

      {/* Backdrop — shown when mobile sidebar is open */}
      {openSidebar && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-40 md:hidden"
          onClick={() => toggleOpenDrawer()}
        />
      )}

      {/* Main content */}
      <div className="flex flex-col flex-1 h-full min-w-0 bg-ghost-white overflow-hidden">
        {/* Mobile top bar */}
        <div className="w-full block md:hidden h-[65px] bg-surface flex-shrink-0">
          <Headerbar toggleOpenDrawer={() => toggleOpenDrawer()} />
        </div>

        {/* Desktop header — breadcrumb + theme toggle */}
        <header className="hidden md:flex items-center gap-3.5 px-7 py-3.5 bg-surface border-b border-borderc flex-shrink-0">
          <div className="flex items-center gap-2 text-[13px] text-text3 min-w-0">
            <span className="truncate">{selectedShop?.shopName || "Netlise"}</span>
            <span className="opacity-50">/</span>
            <span className="text-text1 font-semibold truncate">{screenTitle}</span>
          </div>

          <div className="flex-1" />

          <button
            type="button"
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-borderc text-text2 hover:bg-surface2 hover:text-text1 transition-colors"
          >
            {theme === "dark" ? (
              <SunIcon className="w-4 h-4" />
            ) : (
              <MoonIcon className="w-4 h-4" />
            )}
          </button>
        </header>

        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default connect(
  (state: any) => ({
    trialDetails: state.setting.get("trialDetails"),
    activeSubscriptions: state.setting.get("activeSubscriptions"),
    subscriptionsList: state.setting.get("subscriptionsList"),
    trialLoading: state.setting.get("trialLoading"),
    selectedShop: state.dashboard.get("selectedShop"),
  }),
  {
    logout: Actions.login.logout,
  }
)(Admin);

const PathsWithoutSubscription = [
  "/signin",
  "/signup-subscription",
  "/signup",
  "/forget-password",
  "/business-manage",
  "/otp-screen",
  "/subscription",
];
